import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';
import {
  DEFAULT_WORK_QUEUE_CANCEL_REASONS,
  type WorkQueueCreateOption,
} from './myWorkQueueTypes';
import { formatWorkQueueDate } from './myWorkQueueUtils';
import {
  DEFAULT_MY_WORK_FIELDS,
  facetCounts,
  facetOptions,
  humanizeState,
  type MyWorkAction,
  type MyWorkFieldConfig,
  type MyWorkItem,
  type MyWorkSort,
  type MyWorkType,
  type QueueMemberOption,
  type QueueOperation,
  type StateCategory,
} from './myWorkSor';
import {
  describeSorError,
  useMyWork,
  type MyWorkEmptyReason,
} from './useMyWork';

const SORT_OPTIONS: { key: MyWorkSort; label: string }[] = [
  { key: 'newest', label: 'Newest' },
  { key: 'oldest', label: 'Oldest' },
];

interface TileConfig {
  label: string;
  icon: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
}

/** Status tiles: the record's progress (`source.state_category`) */
const STATUS_TILES: Record<StateCategory, TileConfig> = {
  NOT_STARTED: {
    label: 'Not Started',
    icon: 'mdi:circle-outline',
    textClass: 'text-default-500',
    bgClass: 'bg-default-100',
    borderClass: 'border-l-default-400',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    icon: 'mdi:clock-outline',
    textClass: 'text-warning-600',
    bgClass: 'bg-warning-50',
    borderClass: 'border-l-warning-600',
  },
  DONE: {
    label: 'Completed',
    icon: 'mdi:check',
    textClass: 'text-blue-600',
    bgClass: 'bg-blue-50 dark:bg-blue-500/15',
    borderClass: 'border-l-blue-600',
  },
};

const QUEUE_OPERATION_ICONS: Record<QueueOperation, string> = {
  CLAIM: 'solar:hand-stars-linear',
  ASSIGN: 'solar:user-plus-linear',
  START: 'solar:play-linear',
  WAIT: 'solar:pause-linear',
  RESUME: 'solar:play-linear',
  RELEASE: 'solar:undo-left-linear',
};

const MENU_ICONS: Record<string, string> = {
  ENTITY_UPDATE: 'solar:pen-linear',
  MANUAL_ACKNOWLEDGEMENT: 'solar:bell-linear',
  ENTITY_TRANSITION: 'solar:close-circle-linear',
};

const FIELD_CLASSES = {
  inputWrapper: 'min-h-8 h-8 bg-content1',
  input: 'text-[12.5px]',
};
const SELECT_CLASSES = {
  trigger: 'min-h-8 h-8 bg-content1',
  value: 'text-[12.5px]',
};

const formatCount = (count: number, capped: boolean) =>
  capped ? '10,000+' : count.toLocaleString();

/** Single-select value from a selection change ('' when cleared) */
function getSelectedKey(keys: 'all' | Set<React.Key>): string {
  if (keys === 'all') return '';
  const [next] = keys;
  return next === undefined ? '' : next.toString();
}

/** What the card says about who holds the item; nothing once it's finished (the record's state says it) */
function describeAssignment(item: MyWorkItem): string {
  switch (item.state) {
    case 'READY':
      return 'Unassigned';
    case 'ASSIGNED':
      return item.assigneeName
        ? `Assigned to ${item.assigneeName}`
        : 'Assigned';
    case 'IN_PROGRESS':
      return `${item.claimantName || item.assigneeName || 'Someone'} working`;
    case 'WAITING':
      return item.waitingReason ? `On hold: ${item.waitingReason}` : 'On hold';
    default:
      return '';
  }
}

/** "#1468569 · Total Warranty · Lake Wales, FL" */
function describeItem(item: MyWorkItem): string {
  return [
    `#${item.source?.number ?? item.id}`,
    item.source?.customer,
    item.source?.location,
  ]
    .filter(Boolean)
    .join(' · ');
}

export interface MyWorkProps {
  /** Source fields the list reads; defaults to the inspection's */
  fields?: MyWorkFieldConfig;
  pageSize?: number;
  /** Reasons offered when an action moves the record (e.g. Cancel Inspection); the choice is its comment */
  cancelReasons?: string[];
  /** Opens the item, e.g. its record page; no View button when omitted */
  onView?: (item: MyWorkItem, type: MyWorkType) => void;
  /** Runs ENTITY_UPDATE actions such as Edit (e.g. opens an edit page); hidden when omitted */
  onEdit?: (item: MyWorkItem, action: MyWorkAction, type: MyWorkType) => void;
  /**
   * Delivers a MANUAL_ACKNOWLEDGEMENT action, e.g. sends the reminder; once it resolves the success is
   * recorded in the item's history. Hidden when omitted.
   */
  onManualAction?: (
    item: MyWorkItem,
    action: MyWorkAction,
    type: MyWorkType,
    comment?: string
  ) => Promise<void>;
  /**
   * Shows a "Create {entity}" button per entity type enabled for work queues (`queue_entities`),
   * except to an inspector; receives the pressed entity
   */
  onCreate?: (option: WorkQueueCreateOption) => void;
  /**
   * Change it (e.g. increment) after the host created or changed work outside this view, such as
   * from its Create dialog; the list then reloads once routing has caught up.
   */
  refreshSignal?: number;
}

/** `reasons` set: pick one (record transitions); otherwise free text (hold reason, comments) */
type CommentDialog = {
  item: MyWorkItem;
  action: MyWorkAction;
  label: string;
  reasons?: string[];
};
type AssignDialog = {
  item: MyWorkItem;
  action: MyWorkAction;
  members: QueueMemberOption[] | null;
  error?: string;
};

/**
 * The signed-in user's work in the queues they reach, straight from SOR: a tab per routed type,
 * status tiles, server-side filters and paging, and the queue's own actions.
 * Laid out for the narrow extension panel; cards flow into a grid when there's room.
 */
export function MyWork({
  fields = DEFAULT_MY_WORK_FIELDS,
  pageSize,
  cancelReasons = DEFAULT_WORK_QUEUE_CANCEL_REASONS,
  onView,
  onEdit,
  onManualAction,
  onCreate,
  refreshSignal,
}: Readonly<MyWorkProps>) {
  const work = useMyWork({ fields, pageSize });
  const { refreshAfterRouting } = work;
  // Skips the first value, so mounting doesn't load the list twice
  const lastRefreshSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === lastRefreshSignal.current) {
      return;
    }
    lastRefreshSignal.current = refreshSignal;
    refreshAfterRouting();
  }, [refreshSignal, refreshAfterRouting]);
  const { definition, selectedType, filters, updateFilters, facets } = work;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busyItemId, setBusyItemId] = useState<string | null>(null);
  const [commentDialog, setCommentDialog] = useState<CommentDialog | null>(
    null
  );
  const [comment, setComment] = useState('');
  const [assignDialog, setAssignDialog] = useState<AssignDialog | null>(null);
  const [assignMemberId, setAssignMemberId] = useState('');

  const statusCounts = facetCounts(facets, 'source.state_category');
  const customerOptions = fields.customer
    ? facetOptions(facets, `source.${fields.customer}`)
    : [];
  const stateLabels = useMemo(
    () => new Map(definition?.states.map((state) => [state.key, state]) ?? []),
    [definition]
  );
  const hasActiveFilters = Boolean(
    filters.statusCategory ||
      filters.sourceStates.length ||
      filters.customer ||
      filters.search
  );
  const typeName = selectedType?.name ?? 'Work';
  const pluralName = `${typeName.toLowerCase()}s`;

  const run = async (
    item: MyWorkItem,
    action: MyWorkAction,
    task: () => Promise<void>,
    successMessage?: string
  ) => {
    setBusyItemId(item.id);
    try {
      await task();
      if (successMessage) {
        CtyShowToast(checkType.success, successMessage);
      }
    } catch (error) {
      console.error(`My Work action ${action.data.actionKey} failed`, error);
      CtyShowToast(
        checkType.error,
        describeSorError(
          error,
          `Could not ${action.data.label.toLowerCase()} #${
            item.source?.number ?? ''
          }.`
        )
      );
    } finally {
      setBusyItemId(null);
    }
  };

  const runWithInput = (
    item: MyWorkItem,
    action: MyWorkAction,
    input?: string
  ) => {
    if (!selectedType) {
      return;
    }
    const number = item.source?.number ?? item.id;
    switch (action.data.kind) {
      case 'QUEUE_OPERATION': {
        const operation = action.data.queueOperation as QueueOperation;
        void run(
          item,
          action,
          () =>
            work.runQueueOperation(item, operation, {
              waitingReason: input,
              actionId: action.id,
            }),
          `${action.data.label}: #${number}`
        );
        return;
      }
      case 'ENTITY_TRANSITION':
        void run(
          item,
          action,
          () => work.runEntityTransition(item, action, input),
          action.data.targetState === 'CANCELLED'
            ? `${typeName} #${number} cancelled`
            : `${action.data.label}: #${number}`
        );
        return;
      case 'MANUAL_ACKNOWLEDGEMENT':
        void run(
          item,
          action,
          async () => {
            await onManualAction?.(item, action, selectedType, input);
            await work.recordActionSucceeded(item, action);
          },
          `${action.data.label}: #${number}`
        );
        return;
      case 'ENTITY_UPDATE':
        onEdit?.(item, action, selectedType);
        return;
    }
  };

  const handleAction = (item: MyWorkItem, action: MyWorkAction) => {
    setSelectedId(item.id);
    const operation = action.data.queueOperation;
    if (action.data.kind === 'QUEUE_OPERATION' && operation === 'ASSIGN') {
      setAssignMemberId('');
      setAssignDialog({ item, action, members: null });
      work
        .loadQueueMembers(item.queueId)
        .then((members) =>
          setAssignDialog((current) =>
            current?.item.id === item.id ? { ...current, members } : current
          )
        )
        .catch((error) =>
          setAssignDialog((current) =>
            current?.item.id === item.id
              ? {
                  ...current,
                  members: [],
                  error: describeSorError(
                    error,
                    'Could not load the queue members.'
                  ),
                }
              : current
          )
        );
      return;
    }
    // Moving the record (e.g. Cancel Inspection) is confirmed with a reason from the list
    if (action.data.kind === 'ENTITY_TRANSITION') {
      setComment(cancelReasons[0] ?? '');
      setCommentDialog({
        item,
        action,
        label: 'Reason',
        reasons: cancelReasons,
      });
      return;
    }
    // Putting work on hold always asks why; other actions ask when the action requires a comment
    const isWait =
      action.data.kind === 'QUEUE_OPERATION' && operation === 'WAIT';
    if (isWait || action.data.requireComment) {
      setComment('');
      setCommentDialog({ item, action, label: isWait ? 'Reason' : 'Comment' });
      return;
    }
    runWithInput(item, action);
  };

  const confirmAssign = () => {
    if (!assignDialog) {
      return;
    }
    const { item, action, members } = assignDialog;
    const member = members?.find(
      (candidate) => candidate.id === assignMemberId
    );
    if (!member) {
      return;
    }
    setAssignDialog(null);
    void run(
      item,
      action,
      () =>
        work.runQueueOperation(item, 'ASSIGN', {
          memberId: member.id,
          memberUserId: member.userId,
          actionId: action.id,
        }),
      `Assigned #${item.source?.number ?? item.id} to ${member.label}`
    );
  };

  const renderCard = (item: MyWorkItem) => {
    const sourceState = item.source?.state
      ? stateLabels.get(item.source.state)
      : undefined;
    const tile = STATUS_TILES[sourceState?.category ?? 'NOT_STARTED'];
    const actions = work.actionsFor(item).filter((action) => {
      if (action.data.kind === 'ENTITY_UPDATE') return !!onEdit;
      if (action.data.kind === 'MANUAL_ACKNOWLEDGEMENT')
        return !!onManualAction;
      return true;
    });
    const operations = actions.filter(
      (action) => action.data.kind === 'QUEUE_OPERATION'
    );
    // Edit, then reminders, then actions that move the record (shown in red below a divider)
    const menuActions = actions.filter(
      (action) =>
        action.data.kind === 'ENTITY_UPDATE' ||
        action.data.kind === 'MANUAL_ACKNOWLEDGEMENT'
    );
    const dangerActions = actions.filter(
      (action) => action.data.kind === 'ENTITY_TRANSITION'
    );
    const isBusy = busyItemId === item.id;
    const isSelected = selectedId === item.id;
    const number = item.source?.number ?? item.id;
    const assignment = describeAssignment(item);
    const stateLabel =
      sourceState?.label ??
      (item.source?.state ? humanizeState(item.source.state) : '');

    return (
      <li
        key={item.id}
        className={`flex flex-col gap-1.5 min-w-0 rounded-lg border border-default-200 border-l-4 ${
          tile.borderClass
        } bg-content1 px-2.5 py-[9px] hover:shadow-md ${
          isSelected
            ? 'outline outline-2 -outline-offset-1 outline-primary'
            : ''
        }`}
      >
        <div className="flex gap-2.5 min-w-0">
          {/* Native button selects the card; actions sit beside it, so their clicks never select */}
          <button
            type="button"
            aria-pressed={isSelected}
            onClick={() => setSelectedId(item.id)}
            className="flex flex-1 gap-2.5 min-w-0 text-left cursor-pointer rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            <span
              className={`flex items-center justify-center w-[30px] h-[30px] rounded-full shrink-0 ${tile.bgClass} ${tile.textClass}`}
              title={tile.label}
            >
              <Icon icon={tile.icon} width={17} height={17} />
            </span>
            {/* spans, not divs: a button may only contain phrasing content */}
            <span className="block flex-1 min-w-0">
              <span className="flex items-baseline justify-between gap-2">
                <span className="text-[13.5px] font-bold text-primary truncate">
                  #{number}
                </span>
                <span className="text-[11px] text-default-500 whitespace-nowrap">
                  {item.source?.createdAt
                    ? formatWorkQueueDate(item.source.createdAt)
                    : ''}
                </span>
              </span>
              {(item.source?.customer || item.source?.secondaryNumber) && (
                <span className="block mt-0.5 text-[12.5px] text-foreground truncate">
                  {item.source.customer || item.source.secondaryNumber}
                </span>
              )}
              <span className="flex items-baseline justify-between gap-2 mt-px">
                <span className="text-[11.5px] text-default-500 truncate">
                  {item.source?.location ?? ''}
                </span>
                {stateLabel && (
                  <span
                    className={`text-[10px] font-semibold px-[7px] py-0.5 rounded-full whitespace-nowrap ${tile.bgClass} ${tile.textClass}`}
                  >
                    {stateLabel}
                  </span>
                )}
              </span>
            </span>
          </button>

          <div className="flex flex-col items-end justify-between gap-1.5 shrink-0">
            {menuActions.length + dangerActions.length > 0 ? (
              <Dropdown
                placement="bottom-end"
                classNames={{ content: 'min-w-[212px] p-1' }}
              >
                <DropdownTrigger>
                  <button
                    type="button"
                    disabled={isBusy}
                    className="flex items-center justify-center w-[26px] h-[26px] -mt-[3px] -mr-1 rounded-md text-default-500 hover:bg-default-100 hover:text-primary"
                    aria-label={`Actions for #${number}`}
                  >
                    <Icon icon="mdi:dots-vertical" width={16} height={16} />
                  </button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label={`Actions for #${number}`}
                  onAction={(key) => {
                    const action = actions.find(
                      (candidate) => candidate.id === key
                    );
                    if (action) handleAction(item, action);
                  }}
                >
                  {[...menuActions, ...dangerActions].map((action, index) => {
                    const isDanger = action.data.kind === 'ENTITY_TRANSITION';
                    return (
                      <DropdownItem
                        key={action.id}
                        color={isDanger ? 'danger' : 'default'}
                        className={isDanger ? 'text-danger' : ''}
                        showDivider={
                          index === menuActions.length - 1 &&
                          dangerActions.length > 0
                        }
                        startContent={
                          <Icon
                            icon={MENU_ICONS[action.data.kind]}
                            width={17}
                            height={17}
                          />
                        }
                      >
                        {action.data.label}
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              </Dropdown>
            ) : (
              <span />
            )}
            {onView && selectedType && (
              <button
                type="button"
                className="inline-flex items-center h-6 px-3 rounded-md border border-primary bg-content1 text-primary text-[11.5px] font-semibold hover:bg-primary hover:text-primary-foreground"
                aria-label={`View #${number}`}
                onClick={() => {
                  setSelectedId(item.id);
                  onView(item, selectedType);
                }}
              >
                View
              </button>
            )}
          </div>
        </div>

        {/* Who holds the item, and the queue's assignment steps for it */}
        {(assignment || operations.length > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-1.5 pl-10 pt-1.5 border-t border-default-100">
            <span
              className="text-[11px] text-default-600 truncate min-w-0"
              title={assignment}
            >
              {assignment}
            </span>
            <span className="flex flex-wrap justify-end gap-1">
              {operations.map((action) => (
                <CtyButton
                  key={action.id}
                  size="sm"
                  variant={
                    action.data.queueOperation === 'CLAIM' ||
                    action.data.queueOperation === 'START'
                      ? 'solid'
                      : 'bordered'
                  }
                  color="primary"
                  className="h-6 min-w-0 px-2.5 text-[11.5px] font-semibold"
                  isDisabled={isBusy}
                  onPress={() => handleAction(item, action)}
                  startContent={
                    <Icon
                      icon={
                        QUEUE_OPERATION_ICONS[
                          action.data.queueOperation as QueueOperation
                        ] ?? 'mdi:arrow-right'
                      }
                      width={13}
                      height={13}
                    />
                  }
                >
                  {action.data.label}
                </CtyButton>
              ))}
            </span>
          </div>
        )}
      </li>
    );
  };

  const renderList = () => {
    // A refresh after an action keeps the current cards on screen until the new page arrives
    if (work.listLoading && work.items.length === 0) {
      return (
        <li className="col-span-full py-10 text-center text-[13px] text-default-500">
          Loading {pluralName}…
        </li>
      );
    }
    if (work.emptyReason) {
      return (
        <li className="col-span-full py-10 px-2.5 text-center text-[13px] text-default-500">
          {EMPTY_MESSAGES[work.emptyReason]}
        </li>
      );
    }
    if (work.items.length === 0) {
      return (
        <li className="col-span-full py-10 px-2.5 text-center text-[13px] text-default-500">
          {hasActiveFilters
            ? `No ${pluralName} match these filters.`
            : `No ${pluralName} in your queue.`}
        </li>
      );
    }
    return work.items.map(renderCard);
  };

  return (
    <section
      aria-label={`My ${typeName}s`}
      className="relative flex flex-col flex-1 min-h-0 bg-default-50"
    >
      {work.loadError && (
        <div
          role="alert"
          className="flex flex-col gap-2 px-3 py-2.5 bg-danger-50 text-danger-700 text-[12.5px]"
        >
          <span>
            {work.loadError.needsConnect
              ? 'Connect to the System of Record to load your work.'
              : work.loadError.message}
          </span>
          <div className="flex gap-2">
            {work.loadError.needsConnect && (
              <CtyButton
                size="sm"
                color="primary"
                variant="solid"
                onPress={() => void work.connect()}
              >
                Connect
              </CtyButton>
            )}
            <CtyButton size="sm" variant="bordered" onPress={work.reload}>
              Retry
            </CtyButton>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5 bg-content1 px-2.5 pt-2.5 pb-1.5">
        {/* One tab per routed type; a single type needs no tabs */}
        {work.types.length > 1 && (
          <div
            role="tablist"
            aria-label="Work type"
            className="flex flex-wrap gap-1"
          >
            {work.types.map((type) => {
              const isSelected = type.key === selectedType?.key;
              const count = work.typeCounts[type.definitionId];
              return (
                <button
                  key={type.key}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => work.selectType(type.key)}
                  className={`h-7 px-2.5 rounded-md text-[12.5px] font-semibold ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'text-default-600 hover:bg-default-100'
                  }`}
                >
                  {type.name}
                  {count !== undefined && (
                    <span className="ml-1.5 font-normal opacity-80">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {onCreate && work.createOptions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {work.createOptions.map((option) => (
              <CtyButton
                key={option.definitionId}
                color="primary"
                className="flex-[1_1_160px] h-9 min-h-9 font-semibold text-[13.5px]"
                onPress={() => onCreate(option)}
                startContent={<Icon icon="mdi:plus" width={16} height={16} />}
              >
                Create {option.label}
              </CtyButton>
            ))}
          </div>
        )}

        <div className="grid grid-cols-3 gap-1.5" aria-label="Status">
          {(Object.keys(STATUS_TILES) as StateCategory[]).map((category) => {
            const tile = STATUS_TILES[category];
            const isActive = filters.statusCategory === category;
            return (
              <button
                key={category}
                type="button"
                aria-pressed={isActive}
                onClick={() =>
                  updateFilters({ statusCategory: isActive ? null : category })
                }
                className={`rounded-lg border bg-content1 px-1 py-1.5 text-center ${
                  tile.textClass
                } ${
                  isActive
                    ? 'border-current ring-1 ring-inset ring-current'
                    : 'border-default-200'
                }`}
              >
                <b className="block text-[17px] leading-tight tabular-nums">
                  {statusCounts[category] ?? 0}
                </b>
                <small className="block mt-0.5 text-[9.5px] leading-tight text-default-500">
                  {tile.label}
                </small>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_1fr_96px] gap-1.5 bg-content1 px-2.5 pt-1.5 pb-2.5 border-b border-default-200">
        <Input
          aria-label="Search request number"
          className="col-span-full"
          size="sm"
          variant="bordered"
          placeholder="Search request #"
          value={filters.search}
          onValueChange={(search) => updateFilters({ search })}
          startContent={
            <Icon
              icon="mdi:magnify"
              className="text-default-400 shrink-0"
              width={16}
              height={16}
            />
          }
          classNames={FIELD_CLASSES}
        />
        {fields.customer && (
          <Select
            aria-label="Customer"
            className="min-w-0"
            size="sm"
            variant="bordered"
            placeholder="All customers"
            selectedKeys={filters.customer ? [filters.customer] : []}
            onSelectionChange={(keys) =>
              updateFilters({ customer: getSelectedKey(keys) || null })
            }
            classNames={SELECT_CLASSES}
          >
            {customerOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                textValue={option.value}
              >
                {option.value} ({option.count})
              </SelectItem>
            ))}
          </Select>
        )}
        <Select
          aria-label="State"
          className={`min-w-0 ${fields.customer ? '' : 'col-span-2'}`}
          size="sm"
          variant="bordered"
          placeholder="All states"
          selectedKeys={filters.sourceStates.slice(0, 1)}
          onSelectionChange={(keys) => {
            const state = getSelectedKey(keys);
            updateFilters({ sourceStates: state ? [state] : [] });
          }}
          classNames={SELECT_CLASSES}
        >
          {(definition?.states ?? []).map((state) => (
            <SelectItem key={state.key} value={state.key}>
              {state.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          aria-label="Sort"
          className="min-w-[96px]"
          size="sm"
          variant="bordered"
          disallowEmptySelection
          selectedKeys={[filters.sort]}
          onSelectionChange={(keys) =>
            updateFilters({
              sort: (getSelectedKey(keys) || 'newest') as MyWorkSort,
            })
          }
          classNames={SELECT_CLASSES}
        >
          {SORT_OPTIONS.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </Select>
      </div>

      <div className="flex items-center justify-between px-3 py-[7px] text-[11.5px] text-default-500">
        <span>
          Showing {work.items.length} of{' '}
          {formatCount(work.total, work.totalCapped)} {pluralName}
        </span>
        <button
          type="button"
          className={`text-primary hover:underline ${
            hasActiveFilters ? '' : 'invisible'
          }`}
          onClick={work.clearFilters}
        >
          Clear filters
        </button>
      </div>

      <ul
        aria-busy={work.listLoading}
        className={`flex-1 overflow-auto list-none m-0 px-2.5 pb-3 grid gap-[7px] content-start grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] transition-opacity ${
          work.listLoading && work.items.length > 0 ? 'opacity-60' : ''
        }`}
      >
        {renderList()}
        {!work.listLoading && work.hasMore && (
          <li className="col-span-full flex justify-center pt-1">
            <CtyButton
              size="sm"
              variant="bordered"
              isLoading={work.loadingMore}
              onPress={() => void work.loadMore()}
            >
              Load more
            </CtyButton>
          </li>
        )}
      </ul>

      <Modal
        isOpen={commentDialog !== null}
        onOpenChange={(open) => !open && setCommentDialog(null)}
        size="sm"
        placement="center"
      >
        <ModalContent>
          {(onClose) => {
            const isTransition =
              commentDialog?.action.data.kind === 'ENTITY_TRANSITION';
            return (
              <>
                <ModalHeader className="text-[15px] pb-1">
                  {commentDialog?.action.data.label}
                  {isTransition ? '?' : ''}
                </ModalHeader>
                <ModalBody className="gap-3">
                  {commentDialog && (
                    <p className="text-[12.5px] leading-relaxed text-default-600">
                      {describeItem(commentDialog.item)}
                      {isTransition
                        ? '. It will be removed from your queue.'
                        : ''}
                    </p>
                  )}
                  {commentDialog?.reasons ? (
                    <div className="flex flex-col gap-1">
                      <label
                        htmlFor="my-work-reason"
                        className="text-xs font-semibold text-foreground"
                      >
                        {commentDialog.label}
                      </label>
                      <Select
                        id="my-work-reason"
                        aria-label={commentDialog.label}
                        size="sm"
                        variant="bordered"
                        disallowEmptySelection
                        selectedKeys={comment ? [comment] : []}
                        onSelectionChange={(keys) => {
                          const next = getSelectedKey(keys);
                          if (next) setComment(next);
                        }}
                      >
                        {commentDialog.reasons.map((reason) => (
                          <SelectItem key={reason} value={reason}>
                            {reason}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  ) : (
                    <Textarea
                      label={commentDialog?.label}
                      labelPlacement="outside"
                      variant="bordered"
                      minRows={2}
                      value={comment}
                      onValueChange={setComment}
                    />
                  )}
                </ModalBody>
                <ModalFooter>
                  <CtyButton size="sm" variant="bordered" onPress={onClose}>
                    {isTransition ? `Keep ${typeName.toLowerCase()}` : 'Back'}
                  </CtyButton>
                  <CtyButton
                    size="sm"
                    color={isTransition ? 'danger' : 'primary'}
                    isDisabled={!comment.trim()}
                    onPress={() => {
                      if (commentDialog) {
                        runWithInput(
                          commentDialog.item,
                          commentDialog.action,
                          comment.trim()
                        );
                      }
                      setCommentDialog(null);
                    }}
                  >
                    {commentDialog?.action.data.label}
                  </CtyButton>
                </ModalFooter>
              </>
            );
          }}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={assignDialog !== null}
        onOpenChange={(open) => !open && setAssignDialog(null)}
        size="sm"
        placement="center"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="text-[15px] pb-1">
                {assignDialog?.action.data.label} #
                {assignDialog?.item.source?.number}
              </ModalHeader>
              <ModalBody className="gap-2">
                {assignDialog?.error && (
                  <p className="text-[12.5px] text-danger">
                    {assignDialog.error}
                  </p>
                )}
                <Select
                  aria-label="Assign to"
                  label="Assign to"
                  labelPlacement="outside"
                  size="sm"
                  variant="bordered"
                  placeholder={
                    assignDialog?.members
                      ? 'Choose a member'
                      : 'Loading members…'
                  }
                  isDisabled={!assignDialog?.members?.length}
                  selectedKeys={assignMemberId ? [assignMemberId] : []}
                  onSelectionChange={(keys) =>
                    setAssignMemberId(getSelectedKey(keys))
                  }
                >
                  {(assignDialog?.members ?? []).map((member) => (
                    <SelectItem
                      key={member.id}
                      value={member.id}
                      textValue={member.label}
                    >
                      {member.label}
                      {member.queueRole === 'SUPERVISOR' ? ' (supervisor)' : ''}
                    </SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <CtyButton size="sm" variant="bordered" onPress={onClose}>
                  Back
                </CtyButton>
                <CtyButton
                  size="sm"
                  color="primary"
                  isDisabled={!assignMemberId}
                  onPress={confirmAssign}
                >
                  {assignDialog?.action.data.label ?? 'Assign'}
                </CtyButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </section>
  );
}

const EMPTY_MESSAGES: Record<MyWorkEmptyReason, string> = {
  'no-profile':
    'You have no directory profile in the System of Record yet, so there is no work to show.',
  'no-queues': 'You are not a member of any work queue yet.',
  'no-types': 'Your work queues do not route any work yet.',
};

export default MyWork;
